import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Badge } from '@/components/ui';
import { useCurrentUserName } from '@/features/organization/queries';
import { THEME_LABELS, THEME_PREFERENCES, useThemePreference } from '@/lib/theme';

import { PROFILE_LABELS, type NavigationItem } from './navigation';
import { SignOutDialog } from './SignOutDialog';
import styles from './AccountMenu.module.css';

export interface AccountMenuProps {
  /** `bar`: the avatar in the top bar, opening downwards. `sidebar`: the foot of the sidebar,
   * opening upwards, with the account named beside the avatar unless `collapsed`. */
  readonly variant: 'bar' | 'sidebar';
  readonly collapsed?: boolean | undefined;
  /** Account-level pages for this profile (`navigationLayoutForProfile(...).menu`). */
  readonly items: readonly NavigationItem[];
}

/**
 * Who is signed in, the account-level pages, the palette, and signing out — one component for
 * the top bar and the sidebar, so the two can never offer different things.
 *
 * Signing out asks first (`SignOutDialog`).
 */
export function AccountMenu({ variant, collapsed = false, items }: AccountMenuProps) {
  const { user, profile } = useAuth();
  const [theme, setTheme] = useThemePreference();
  const [open, setOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const fullName = useCurrentUserName();
  const email = user?.email ?? 'Sessão anônima';
  // A person is named by their name; the e-mail is the fallback until (or unless) it loads.
  const displayName = fullName ?? email;
  const initials = initialsOf(fullName) ?? email.slice(0, 2).toUpperCase();
  const profileLabel = profile === null ? 'Sem perfil' : PROFILE_LABELS[profile];

  const showName = variant === 'sidebar' && !collapsed;

  return (
    <div className={[styles.root, styles[variant]].join(' ')} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => {
          setOpen((value) => !value);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Conta: ${displayName}, ${profileLabel}`}
        title={collapsed ? `${displayName} · ${profileLabel}` : undefined}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials}
        </span>
        {showName ? (
          <span className={styles.identity} aria-hidden="true">
            <span className={styles.email}>{displayName}</span>
            <span className={styles.profile}>{profileLabel}</span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div className={styles.menu} role="menu">
          <div className={styles.menuHeader}>
            <p className={styles.menuName}>{displayName}</p>
            {fullName !== undefined ? <p className={styles.menuEmail}>{email}</p> : null}
            <p className={styles.menuProfile}>
              <Badge tone={profile === null ? 'warning' : 'brand'} withDot>
                {profileLabel}
              </Badge>
            </p>
          </div>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              role="menuitem"
              viewTransition
              className={({ isActive }) =>
                [styles.menuItem, isActive ? styles.menuItemActive : null].filter(Boolean).join(' ')
              }
              onClick={() => {
                setOpen(false);
              }}
            >
              {item.label}
            </NavLink>
          ))}
          {items.length > 0 ? <hr className={styles.rule} /> : null}
          {/* The menu stays open on a theme choice: the point is to see the page change. */}
          <div className={styles.themeRow} role="group" aria-label="Aparência">
            <span className={styles.themeLabel} aria-hidden="true">
              Aparência
            </span>
            <div className={styles.themeOptions}>
              {THEME_PREFERENCES.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="menuitemradio"
                  aria-checked={theme === option}
                  className={styles.themeOption}
                  onClick={() => {
                    setTheme(option);
                  }}
                >
                  {THEME_LABELS[option]}
                </button>
              ))}
            </div>
          </div>
          <hr className={styles.rule} />
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              setConfirmingSignOut(true);
            }}
          >
            Sair
          </button>
        </div>
      ) : null}

      <SignOutDialog
        open={confirmingSignOut}
        onClose={() => {
          setConfirmingSignOut(false);
        }}
      />
    </div>
  );
}

/** "Maria da Silva Souza" → "MS": first and last word, so a middle name never wins the avatar. */
function initialsOf(fullName: string | undefined): string | undefined {
  const words = fullName?.split(/\s+/u).filter((word) => word.length > 0) ?? [];
  const first = words[0];
  if (first === undefined) {
    return undefined;
  }
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  return `${first.charAt(0)}${last?.charAt(0) ?? ''}`.toUpperCase();
}
